import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { conditionPhotoTypeService } from '../services/api';
import { ConditionPhotoType } from '../types';

export interface ConditionPhotoTypeState {
  types: ConditionPhotoType[];
  loading: boolean;
  error: string | null;
}

const initialState: ConditionPhotoTypeState = {
  types: [],
  loading: false,
  error: null,
};

export const fetchConditionPhotoTypes = createAsyncThunk(
  'conditionPhotoTypes/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await conditionPhotoTypeService.getTypes();
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch condition photo types');
    }
  }
);

export const createConditionPhotoType = createAsyncThunk(
  'conditionPhotoTypes/create',
  async (data: {
    code: string;
    name: string;
    name_en?: string;
    description?: string;
    icon?: string;
    color?: string;
    is_active?: boolean;
    sort_order?: number;
  }, { rejectWithValue }) => {
    try {
      const response = await conditionPhotoTypeService.createType(data);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create condition photo type');
    }
  }
);

export const updateConditionPhotoType = createAsyncThunk(
  'conditionPhotoTypes/update',
  async ({ id, data }: {
    id: number;
    data: {
      code?: string;
      name?: string;
      name_en?: string;
      description?: string;
      icon?: string;
      color?: string;
      is_active?: boolean;
      sort_order?: number;
    }
  }, { rejectWithValue }) => {
    try {
      const response = await conditionPhotoTypeService.updateType(id, data);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update condition photo type');
    }
  }
);

export const deleteConditionPhotoType = createAsyncThunk(
  'conditionPhotoTypes/delete',
  async (id: number, { rejectWithValue }) => {
    try {
      await conditionPhotoTypeService.deleteType(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete condition photo type');
    }
  }
);

const conditionPhotoTypeSlice = createSlice({
  name: 'conditionPhotoTypes',
  initialState,
  reducers: {
    clearPhotoTypeError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConditionPhotoTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConditionPhotoTypes.fulfilled, (state, action: PayloadAction<ConditionPhotoType[]>) => {
        state.loading = false;
        state.types = action.payload;
      })
      .addCase(fetchConditionPhotoTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createConditionPhotoType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createConditionPhotoType.fulfilled, (state, action: PayloadAction<ConditionPhotoType>) => {
        state.loading = false;
        state.types.push(action.payload);
      })
      .addCase(createConditionPhotoType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateConditionPhotoType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateConditionPhotoType.fulfilled, (state, action: PayloadAction<ConditionPhotoType>) => {
        state.loading = false;
        const index = state.types.findIndex((type) => type.id === action.payload.id);
        if (index !== -1) {
          state.types[index] = action.payload;
        }
      })
      .addCase(updateConditionPhotoType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteConditionPhotoType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteConditionPhotoType.fulfilled, (state, action: PayloadAction<number>) => {
        state.loading = false;
        state.types = state.types.filter((type) => type.id !== action.payload);
      })
      .addCase(deleteConditionPhotoType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearPhotoTypeError } = conditionPhotoTypeSlice.actions;
export default conditionPhotoTypeSlice.reducer;

